'use client';

import { Suspense, useCallback, useState } from 'react';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { getCountryCodeByTerm } from '@sola/contentful-model/lib/article.js';
import {
  ArticleCountriesEnum,
  ArticleTermsEnum,
  ArticleThemesEnum,
  ArticleTopicsEnum,
  ArticleTypesEnum,
  articleCountries,
  articleThemes,
  articleTopics,
} from '@sola/contentful-model/lib/taxonomy-values.js';

import { ArticleFilter } from './article-filter';
import { ArticleList } from './article-list';

const ArticleFilterSkeleton = () => (
  <div className="ArticleFilter">
    <div className="ArticleFilter__container flex flex-col md:flex-row md:justify-between md:align-center gap-comfy-lg">
      <div className="body-md md:align-center">
        <Skeleton height={20} width={120} />
      </div>
      <div className="flex align-end md:justify-end md:align-center gap-comfy-sm">
        <Skeleton height={40} width={140} borderRadius="0.5rem" />
        <Skeleton height={40} width={80} borderRadius="0.5rem" />
      </div>
    </div>
  </div>
);

type TaxonomyProps = Partial<{
  theme: ArticleThemesEnum;
  topic: ArticleTopicsEnum;
  country: ArticleCountriesEnum;
}>;

type ArticleControlsProps = {
  seoLevel?: number;
  articleType?: ArticleTypesEnum;
  term?: ArticleTermsEnum;
};

const getTaxonomyProps = (term: string): TaxonomyProps => {
  if (!term) {
    return {};
  }
  if (articleThemes.includes(term as ArticleThemesEnum)) {
    return { theme: term } as TaxonomyProps;
  }
  if (articleTopics.includes(term as ArticleTopicsEnum)) {
    return { topic: term } as TaxonomyProps;
  }
  const countryCode = getCountryCodeByTerm(term);
  if (countryCode && articleCountries.includes(term)) {
    return { country: countryCode } as TaxonomyProps;
  }
  return {};
};

export const ArticleControls = ({
  seoLevel,
  articleType,
  term,
}: ArticleControlsProps) => {
  const [articleCount, setArticleCount] = useState(0);

  const onArticleLoad = useCallback(
    ({ totalCount }: { totalCount: number }) => {
      setArticleCount(totalCount);
    },
    [],
  );

  const taxonomyProps: TaxonomyProps = term ? getTaxonomyProps(term) : {};

  const articleFilterCount = term ? articleCount : undefined;

  return (
    <div className="grid gap-comfy-xl md:gap-roomy-sm">
      <Suspense fallback={<ArticleFilterSkeleton />}>
        <ArticleFilter articleCount={articleFilterCount} />
      </Suspense>
      <ArticleList
        seoLevel={seoLevel}
        articleType={articleType}
        onArticleLoad={onArticleLoad}
        {...taxonomyProps}
      />
    </div>
  );
};
